import React, { useContext, useEffect, useState, useMemo } from 'react'
import { debounce, isEqual } from 'lodash';
import { useTranslation } from 'react-i18next';
import { stringifyJSONAttributes, stringToBooleanConversion } from '../../../utils/utilFunctions';
import TPCheckbox from "../../TemplateProperties/TPCheckbox";
import { Autocomplete, Button, FormControl, TextField } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { api } from '../../../redux/api';
import { MoreHorizOutlined } from '@mui/icons-material';
import WorkflowSessionModal from '../../ItemProperties/WorkflowSessionModal';

const DataStream = ({ connectionDetails, handleBodyContent, screen, disableControl, useConnection, repoPDC }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { account } = useSelector((state) => state.queryParams);
  const { leftTree } = useSelector((state) => state.treeData[state.treeData.currentScreen]);
  const leftSelectedNode = leftTree?.tree?.body?.selectedNode
  const [workFlowsList, setWorkFlowsList] = useState([]);
  const [callWorkflow, setCallWorkflow] = useState(false);
  const [workflowGroups, setWorkflowGroups] = useState([]);
  const [workflowGroup, setWorkflowGroup] = useState('');
  const [workflowToCalls, setWorkflowsToCalls] = useState([]);
  const [workflowToCall, setWorkflowToCall] = useState('');
  const [runConcurrently, setRunConcurrently] = useState(false);
  const [runDistributed, setRunDistributed] = useState(false);
  const [maxConcurrent, setMaxConcurrent] = useState(0);
  const [ignoreCallWorkflow, setIgnoreCallWorkflow] = useState(false);
  const [content, setContent] = useState(null);
  const [valueLabel, setValueLabel] = useState("");
  const [workflowSessionModal, setWorkflowSessionModal] = useState({
    open: false,
    isFirstTime: false,
  });
  const debouncedContentUpdate = debounce(handleBodyContent, 500);
  const [showWorkflowNotFoundMessage, setShowWorkflowNotFoundMessage] = useState(false);
  const onInputChange = (name, value) => {
   
    const isSendData = screen === 'SEND_DATA';
  
    if (isSendData) {
      if (name === 'CWF_WORKFLOW') {
        const isValidValue = value !== t('SELECT') || value !== '' || value !== null || value !== undefined;
        setContent({
          add: {
            CWF_WORKFLOW: isValidValue ? typeof value === 'object' ? value?.name : value : null,
            CWF_ACCOUNT: isValidValue ? account : null
          }
        });
      }  else if (name === 'CWF_WF_GROUP') {
        setContent({
         add: {
          [name]: value,
          CWF_WORKFLOW: null,
          CWF_ACCOUNT: null
         }
        });
      }  else {
        setContent({ [name]: value });
      }
    } else {
      if (name === 'CWF_WORKFLOW') {
        setContent(prev => ({
          ...prev,
          [name]: typeof value === 'object' ? value?.name : value,
          CWF_ACCOUNT: value !== t('SELECT') ? account : null
        }));
      } else if (name === 'CWF_WF_GROUP') {
        setContent(prev => {
          const newContent = { ...prev, [name]: value };
          delete newContent.CWF_WORKFLOW;
          delete newContent.CWF_ACCOUNT;
          return newContent;
        });
      }  else {
        setContent(prev => ({
          ...prev,
          [name]: value
        }));
      }
    }
  };
  
  const workflowMappings = {
    [t("LBL_WF_GROUP")]: {
      stateSetter: setWorkflowGroup,
      key: "CWF_WF_GROUP",
      value: workflowGroup,
    },
    [t("LBL_WORKFLOW_TO_CALL")]: {
      stateSetter: setWorkflowToCall,
      key: "CWF_WORKFLOW",
      value: workflowToCall,
    },
  };
  const valuesHandler = (value) => {
    if (workflowMappings[valueLabel]) {
      const { stateSetter, key } = workflowMappings[valueLabel];
      stateSetter(value);
      setContent({
        [key]: value,
      });
      // onInputChange(key, value);
    }
  };

  const updateCallToWorkflows = (wfgName) => {
    const wfg = workFlowsList?.find((workflow) => workflow?.name === wfgName);
    if (wfg?.workflows && Array.isArray(wfg.workflows) && wfg.workflows.length > 0) {
      setWorkflowToCall(t('SELECT'));
      
    } else {
      setWorkflowToCall('');
      
    }
  }

  useEffect(() => {
    if (callWorkflow) {
      dispatch(
        api.endpoints.getWorkflowsCopy.initiate({ account }, { forceRefetch: true })
      ).then((res) => {
        if (res?.data?.current_state === "SUCCESS" && res?.data?.data?.workflow_groups && res?.data?.data?.workflow_groups?.length > 0) {
          setWorkFlowsList(res?.data?.data?.workflow_groups);
          const workflows = res?.data?.data?.workflow_groups?.map((workflow) => workflow?.name) || [];
          const data = res?.data?.data?.workflow_groups?.find((workflow) => workflow?.name === workflowGroup)?.workflows?.filter((workflow) => workflow?.type === "WORKFLOW");
          
          setWorkflowGroups(workflows);
          setWorkflowsToCalls([t('SELECT'), ...(data|| [])]);
          
          // Set message visibility based on data and workflowToCall
          setShowWorkflowNotFoundMessage(
            (!data && workflowToCall && workflowToCall !== t('SELECT')) || 
            (data && workflowToCall && workflowToCall !== t('SELECT') && !data.some(wf => wf.name === workflowToCall))
          );

          if (!workflowToCall || workflowToCall === t('SELECT')) {
            if (data?.length === 1) {
              setWorkflowToCall(data?.[0]);
              onInputChange('CWF_WORKFLOW', data?.[0]);
            } else {
              setWorkflowToCall(t('SELECT'));
            }
          }
        } else {
          setWorkflowGroups([]);
          setWorkflowsToCalls([]);
          setShowWorkflowNotFoundMessage(false);
        }
      });
    }
  }, [callWorkflow, workflowGroup])

  useEffect(() => {
    setCallWorkflow(stringToBooleanConversion(connectionDetails?.USE_CALL_WF_FOR_DATASTREAM));
    setWorkflowGroup(connectionDetails?.CWF_WF_GROUP || (leftSelectedNode?.type === 'workflowGroup' ? leftSelectedNode?.title : leftSelectedNode?.workflowGroup) || '');
    
    // Only update workflow if connectionDetails changes
    if (connectionDetails?.CWF_WORKFLOW) {
      setWorkflowToCall(connectionDetails.CWF_WORKFLOW);
    } else {
      setWorkflowToCall(t('SELECT'));
    }
    
    setRunConcurrently(stringToBooleanConversion(connectionDetails?.CWF_RUN_CONCURRENT));
    setRunDistributed(stringToBooleanConversion(connectionDetails?.CWF_RUN_DISTRIBUTED));
    setMaxConcurrent(connectionDetails?.CWF_MAX_CONCURRENT_THREADS || '');
    setIgnoreCallWorkflow(stringToBooleanConversion(connectionDetails?.CWF_IGNORE_ERRORS));
    setContent({ ...connectionDetails });
  }, [connectionDetails]);

  useEffect(() => {
    if (!isEqual(content, connectionDetails)) debouncedContentUpdate(content);
  }, [content]);

  // Add getFontWeight helper function
  const getFontWeight = (control, currentValue, connectionDetails, useConnection) => {
    if(!repoPDC) return "normal";
    // if (!(control in repoPDC)) return "normal";
    return repoPDC?.[control] !== currentValue && useConnection === "preDefined"
      ? "bold"
      : "normal";
  };
  
  // Add fontWeights object with useMemo
  const fontWeights = useMemo(() => {
    return {
      CWF_WF_GROUP: getFontWeight(
        "CWF_WF_GROUP",
        workflowGroup,
        connectionDetails,
        useConnection
      ),
      CWF_WORKFLOW: getFontWeight(
        "CWF_WORKFLOW",
        workflowToCall,
        connectionDetails,
        useConnection
      ),
      CWF_MAX_CONCURRENT_THREADS: getFontWeight(
        "CWF_MAX_CONCURRENT_THREADS",
        maxConcurrent,
        connectionDetails,
        useConnection
      )
    };
  }, [workflowGroup, workflowToCall, maxConcurrent, connectionDetails, useConnection]);

  return (
    <>
      <div>
        <TPCheckbox
          label={t("LBL_DISPLAY_CALL_WORKFLOW_TO_PROCESS")}
          checked={callWorkflow}
          disabled={disableControl || false}
          handleCheck={(checked) => {
            onInputChange(
              "USE_CALL_WF_FOR_DATASTREAM",
              stringifyJSONAttributes(checked)
            );
            setCallWorkflow(checked);
          }}
        />
      </div>
      {callWorkflow && (
        <>
          <div style={{ display: "flex", gap: "4px" }}>
            <Autocomplete
              disablePortal
              className="width-100"
              options={workflowGroups || []}
              value={workflowGroup}
              disabled={disableControl || false}
              sx={{ maxHeight: 300 }}
              getOptionLabel={(option) => option}
              onChange={(e, value) => {
                onInputChange("CWF_WF_GROUP", value);
                setWorkflowGroup(value);
                updateCallToWorkflows(value);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("LBL_WF_GROUP")}
                  disabled={disableControl || false}
                  variant="outlined"
                  size="small"
                  autoComplete="off"
                  sx={{ input: { fontWeight: fontWeights.CWF_WF_GROUP } }}
                />
              )}
            />
            {screen && screen === "SEND_DATA" && (
              <Button
                size="small"
                disabled={disableControl || false}
                variant="outlined"
                onClick={() => {
                  setValueLabel(t("LBL_WF_GROUP"));
                  setWorkflowSessionModal({ open: true, isFirstTime: true });
                }}
                sx={{ width: "40px", minWidth: "40px" }}>
                <MoreHorizOutlined fontSize="small" />
              </Button>
            )}
          </div>
          <div style={{ display: "flex", gap: "4px" }}>
            <Autocomplete
              disablePortal
              options={workflowToCalls || []}
              value={workflowToCall}
              className="width-100"
              disabled={disableControl || false}
              sx={{ maxHeight: 300 }}
              getOptionLabel={(option) => {
                if (!option) return "";
                return typeof option === "object" ? option.name || "" : option;
              }}
              onChange={(e, value) => {
                onInputChange("CWF_WORKFLOW", value);
                setWorkflowToCall(value);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("LBL_WORKFLOW_TO_CALL")}
                  disabled={disableControl || false}
                  variant="outlined"
                  size="small"
                  autoComplete="off"
                  sx={{ input: { fontWeight: fontWeights.CWF_WORKFLOW } }}
                />
              )}
            />
            {screen && screen === "SEND_DATA" && (
              <Button
                size="small"
                disabled={disableControl || false}
                variant="outlined"
                onClick={() => {
                  setValueLabel(t("LBL_WORKFLOW_TO_CALL"));
                  setWorkflowSessionModal({ open: true, isFirstTime: true });
                }}
                sx={{ width: "40px", minWidth: "40px" }}>
                <MoreHorizOutlined fontSize="small" />
              </Button>
            )}
          </div>
          {showWorkflowNotFoundMessage && (
            <div
              style={{
                fontStyle: "italic",
                fontWeight: 500,
                marginTop: "8px",
              }}>
              {t("*The selected workflow does not exist in the chosen workflow group.")}
            </div>
          )}
          <div className="flex">
            <TPCheckbox
              label={t("LBL_RUN_CONCURRENTLY")}
              checked={runConcurrently}
              disabled={disableControl || false}
              handleCheck={(checked) => {
                onInputChange(
                  "CWF_RUN_CONCURRENT",
                  stringifyJSONAttributes(checked)
                );
                setRunConcurrently(checked);
              }}
            />
            <TPCheckbox
              label={t("LBL_RUN_DISTRIBUTED")}
              checked={runDistributed}
              disabled={disableControl || false}
              handleCheck={(checked) => {
                onInputChange(
                  "CWF_RUN_DISTRIBUTED",
                  stringifyJSONAttributes(checked)
                );
                setRunDistributed(checked);
              }}
            />
          </div>
          <FormControl className="width-100" size="small">
            <TextField
              variant="outlined"
              size="small"
              label={t("MAX_CURR_TRANS")}
              type="number"
              value={maxConcurrent || 0}
              disabled={disableControl || false}
              onChange={(e) => {
                onInputChange(e.target.name, e.target.value);
                setMaxConcurrent(e.target.value);
              }}
              className={"width-100"}
              name={"CWF_MAX_CONCURRENT_THREADS"}
              autoComplete="off"
              sx={{
                input: { fontWeight: fontWeights.CWF_MAX_CONCURRENT_THREADS },
              }}
            />
          </FormControl>
          <div>
            <TPCheckbox
              label={t("LBL_IGNORE_CWF_ERRORS")}
              checked={ignoreCallWorkflow}
              disabled={disableControl || false}
              handleCheck={(checked) => {
                onInputChange(
                  "CWF_IGNORE_ERRORS",
                  stringifyJSONAttributes(checked)
                );
                setIgnoreCallWorkflow(checked);
              }}
            />
          </div>
          <WorkflowSessionModal
            valueLabel={valueLabel}
            showAccountData={true}
            showCallingWorkflow={true}
            showSystemData={true}
            showWorkflowSession={true}
            showDateAndTime={true}
            openModal={workflowSessionModal}
            setModalOpen={setWorkflowSessionModal}
            handleClose={() =>
              setWorkflowSessionModal({ open: false, isFirstTime: false })
            }
            initialValue={workflowMappings[valueLabel]?.value}
            valuesHandler={(value) => {
              valuesHandler(value);
            }}
          />
        </>
      )}
    </>
  );
}

export default DataStream